import { createContext, useContext, useMemo, useState, useSyncExternalStore, type ReactNode } from "react";
import type { DataMessagePartComponent } from "@assistant-ui/react";
import { Check, CheckCircle2, LoaderCircle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import type { LeadChange } from "../leads/types";
import { getReceiptSnapshot, setReceiptStatus, subscribeReceipts } from "./receiptStore";
import type { ChangeReceipt } from "./types";

interface ReceiptContextValue {
  owner: string;
  apply: (changes: LeadChange[]) => Promise<void>;
}

const ReceiptContext = createContext<ReceiptContextValue | null>(null);

export function ReceiptProvider({
  owner,
  apply,
  children,
}: ReceiptContextValue & { children: ReactNode }) {
  const value = useMemo(() => ({ owner, apply }), [owner, apply]);
  return <ReceiptContext.Provider value={value}>{children}</ReceiptContext.Provider>;
}

function reverseChanges(changes: LeadChange[]): LeadChange[] {
  return [...changes].reverse().map((change) => ({
    type: change.after ? (change.before ? "update" : "delete") : "create",
    before: change.after,
    after: change.before,
  }));
}

export const LeadChangeReceipt: DataMessagePartComponent<ChangeReceipt> = ({ data: receipt }) => {
  const context = useContext(ReceiptContext);
  const statuses = useSyncExternalStore(subscribeReceipts, getReceiptSnapshot, getReceiptSnapshot);
  const [busy, setBusy] = useState<"confirm" | "undo" | null>(null);
  if (!context || !receipt?.id) return null;
  const status = statuses[receipt.id] ?? "saved";

  const confirm = async () => {
    setBusy("confirm");
    try {
      await setReceiptStatus(context.owner, receipt.id, "confirmed");
      toast.success("已确认留档；数据此前已默认保存");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "确认失败");
    } finally {
      setBusy(null);
    }
  };

  const undo = async () => {
    setBusy("undo");
    try {
      await context.apply(reverseChanges(receipt.changes));
      await setReceiptStatus(context.owner, receipt.id, "undone");
      toast.success("已撤销这次变更，并恢复快照");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "撤销失败");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="my-3 flex flex-col gap-3 rounded-xl border bg-card p-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-receipt-id={receipt.id}>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <CheckCircle2 className={`size-4 shrink-0 ${status === "undone" ? "text-muted-foreground" : "text-emerald-600"}`} />
        <span>{status === "confirmed" ? "已确认留档" : status === "undone" ? "本次变更已撤销" : "已默认保存，可确认或撤销"}</span>
      </div>
      {status !== "undone" && (
        <div className="flex gap-2">
          {status === "saved" && (
            <Button size="sm" variant="outline" onClick={() => void confirm()} disabled={busy !== null} aria-label="Confirm 确认留档">
              {busy === "confirm" ? <LoaderCircle className="animate-spin" /> : <Check />}Confirm
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => void undo()} disabled={busy !== null} aria-label="Undo 撤销变更">
            {busy === "undo" ? <LoaderCircle className="animate-spin" /> : <Undo2 />}Undo
          </Button>
        </div>
      )}
    </div>
  );
}
